import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Loader2, Tag, Package, Search, Camera, Sparkles, Plus, Boxes, Wand2, Calendar } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

// Default occasions - will be replaced by API data
const DEFAULT_OCCASIONS = [
  { value: "none", label: "None" },
  { value: "other", label: "Other" },
];

const CreateDesignModal = ({ onClose, onCreated }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [occasion, setOccasion] = useState('none');
  const [occasions, setOccasions] = useState(DEFAULT_OCCASIONS);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  // Camera
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch occasions from API
  useEffect(() => {
    const fetchOccasions = async () => {
      try {
        // Use the public filter-options endpoint
        const res = await fetch(`${BACKEND_URL}/api/designs/filter-options`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          const occasionValues = data.all_occasions || [];
          // Convert to {value, label} format and add "None" at start
          const occasionOptions = occasionValues.map(v => ({
            value: v,
            label: v.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          }));
          setOccasions([{ value: "none", label: "None" }, ...occasionOptions]);
        }
      } catch (err) {
        console.error('Failed to fetch occasions:', err);
      }
    };
    fetchOccasions();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !showCamera && !submitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showCamera, submitting]);

  // Technique tags
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Unified products list: each item has { name, brand?, item_id?, category?, source: 'stash'|'ai_detected'|'manual' }
  const [products, setProducts] = useState([]);
  const [stashItems, setStashItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [manualProductInput, setManualProductInput] = useState('');
  const [aiSuggestedOnce, setAiSuggestedOnce] = useState(false);

  const fileInputRef = useRef(null);
  const tagInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch stash items for product picker
  useEffect(() => {
    const fetchStash = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stash`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setStashItems(Array.isArray(data) ? data : (data.items || []));
        }
      } catch {
        // Optional
      }
    };
    fetchStash();
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Fetch tag suggestions
  useEffect(() => {
    if (!tagInput.trim()) {
      setTagSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/designs/technique-tags?q=${encodeURIComponent(tagInput)}`);
        if (res.ok) {
          const data = await res.json();
          setTagSuggestions((data.tags || []).filter((t) => !tags.includes(t)));
        }
      } catch {
        // Optional
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [tagInput, tags]);

  // --- Image handling ---
  const setImageFromFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const handleImageChange = (e) => {
    setImageFromFile(e.target.files?.[0]);
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  // --- Camera ---
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } },
      });
      streamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      setError('Camera access denied. Please use the upload option instead.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        setImageFromFile(file);
      }
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // --- AI Suggest (enhanced with product suggestions) ---
  const handleAiSuggest = async () => {
    if (!image) return;
    setAnalyzing(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', image);
      // Send stash items for cross-referencing
      const stashPayload = stashItems.map(s => ({
        item_id: s.item_id,
        name: s.name,
        brand: s.brand || '',
        category: s.category || '',
      }));
      formData.append('stash_items', JSON.stringify(stashPayload));

      const res = await fetch(`${BACKEND_URL}/api/designs/analyze-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.title) setTitle(data.title);
        if (data.description) setDescription(data.description);
        if (data.technique_tags?.length > 0) setTags(data.technique_tags.slice(0, 10));

        // Build product list from AI suggestions
        const newProducts = [];
        if (data.matched_stash_items?.length > 0) {
          for (const item of data.matched_stash_items) {
            // Avoid duplicates
            if (!newProducts.some(p => p.name?.toLowerCase() === item.name?.toLowerCase())) {
              newProducts.push({
                item_id: item.item_id || '',
                name: item.name || '',
                brand: item.brand || '',
                category: item.category || '',
                source: 'stash',
              });
            }
          }
        }
        if (data.detected_materials?.length > 0) {
          for (const item of data.detected_materials) {
            if (!newProducts.some(p => p.name?.toLowerCase() === item.name?.toLowerCase())) {
              newProducts.push({
                name: item.name || '',
                source: 'ai_detected',
              });
            }
          }
        }
        // Merge with existing products, avoiding duplicates
        setProducts(prev => {
          const existing = new Set(prev.map(p => p.name?.toLowerCase()));
          const merged = [...prev];
          for (const np of newProducts) {
            if (!existing.has(np.name?.toLowerCase())) {
              merged.push(np);
            }
          }
          return merged.slice(0, 20);
        });
        setAiSuggestedOnce(true);
      } else {
        const err = await res.json();
        setError(err.detail || 'AI analysis failed');
      }
    } catch {
      setError('Failed to analyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  // --- Tags ---
  const addTag = (tag) => {
    const t = tag.toLowerCase().trim();
    if (t && !tags.includes(t) && tags.length < 10) {
      setTags((prev) => [...prev, t]);
    }
    setTagInput('');
    setShowTagSuggestions(false);
    tagInputRef.current?.focus();
  };

  const removeTag = (tag) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  // --- Products ---
  const removeProduct = (index) => {
    setProducts(prev => prev.filter((_, i) => i !== index));
  };

  const addStashProduct = (item) => {
    if (products.length >= 20) return;
    const alreadyAdded = products.some(
      p => p.item_id === item.item_id || p.name?.toLowerCase() === item.name?.toLowerCase()
    );
    if (!alreadyAdded) {
      setProducts(prev => [...prev, {
        item_id: item.item_id,
        name: item.name,
        brand: item.brand || '',
        category: item.category || '',
        source: 'stash',
      }]);
    }
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const addManualProduct = () => {
    const name = manualProductInput.trim();
    if (!name || products.length >= 20) return;
    const alreadyAdded = products.some(p => p.name?.toLowerCase() === name.toLowerCase());
    if (!alreadyAdded) {
      setProducts(prev => [...prev, { name, source: 'manual' }]);
    }
    setManualProductInput('');
  };

  const handleManualProductKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addManualProduct();
    }
  };

  const filteredStash = stashItems.filter((item) => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(q) ||
      (item.brand || '').toLowerCase().includes(q) ||
      (item.category || '').toLowerCase().includes(q)
    );
  }).filter(item =>
    !products.some(p => p.item_id === item.item_id || p.name?.toLowerCase() === item.name?.toLowerCase())
  );

  // --- Submit ---
  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('is_public', isPublic.toString());
      formData.append('occasion', occasion);
      formData.append('technique_tags', JSON.stringify(tags));
      formData.append('products_used', JSON.stringify(products));
      if (image) {
        formData.append('image', image);
      }

      const res = await fetch(`${BACKEND_URL}/api/designs`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (res.ok) {
        onCreated();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to create design');
      }
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const sourceBadge = (source) => {
    switch (source) {
      case 'stash':
        return <span className="shrink-0 px-1.5 py-0.5 bg-sage/15 text-sage text-[10px] rounded-full font-medium">Stash</span>;
      case 'ai_detected':
        return <span className="shrink-0 px-1.5 py-0.5 bg-rose/15 text-rose text-[10px] rounded-full font-medium">AI Detected</span>;
      default:
        return <span className="shrink-0 px-1.5 py-0.5 bg-gold/15 text-gold-dark text-[10px] rounded-full font-medium">Manual</span>;
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" data-testid="create-design-modal">
      <div className="bg-white rounded-xl border border-beige shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-beige">
          <h2 className="text-lg font-medium text-charcoal">Add Design</h2>
          <button onClick={onClose} className="p-1 text-gray-soft hover:text-charcoal rounded transition-colors" data-testid="close-design-modal">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Image upload / camera */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Photo</label>

            {/* Camera view */}
            {showCamera && (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black mb-2">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                <div className="absolute bottom-3 inset-x-0 flex justify-center gap-3">
                  <button
                    onClick={capturePhoto}
                    className="w-14 h-14 rounded-full bg-white border-4 border-sage shadow-lg hover:scale-105 transition-transform"
                    data-testid="capture-photo-btn"
                  />
                  <button onClick={stopCamera} className="px-4 py-2 bg-charcoal/60 text-white rounded-lg text-sm hover:bg-charcoal/80">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Preview */}
            {!showCamera && imagePreview && (
              <div className="relative aspect-video rounded-lg overflow-hidden bg-cream border border-beige mb-2">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                <button
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-1.5 bg-charcoal/60 text-white rounded-full hover:bg-charcoal/80 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Upload / Snap buttons with drop zone */}
            {!showCamera && !imagePreview && (
              <div
                className={`flex gap-3 rounded-lg transition-colors ${isDragging ? 'ring-2 ring-sage ring-offset-2' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) setImageFromFile(file);
                }}
              >
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex-1 aspect-[2/1] rounded-lg border-2 border-dashed bg-cream/50 flex flex-col items-center justify-center gap-1.5 transition-colors ${
                    isDragging ? 'border-sage bg-sage/5' : 'border-beige hover:border-sage/50'
                  }`}
                  data-testid="upload-image-btn"
                >
                  <Upload size={22} className={isDragging ? 'text-sage' : 'text-gray-soft'} />
                  <span className={`text-xs ${isDragging ? 'text-sage font-medium' : 'text-gray-soft'}`}>
                    {isDragging ? 'Drop here' : 'Upload or drop'}
                  </span>
                </button>
                <button
                  onClick={startCamera}
                  className="flex-1 aspect-[2/1] rounded-lg border-2 border-dashed border-beige hover:border-sage/50 bg-cream/50 flex flex-col items-center justify-center gap-1.5 transition-colors"
                  data-testid="snap-photo-btn"
                >
                  <Camera size={22} className="text-gray-soft" />
                  <span className="text-xs text-gray-soft">Snap Photo</span>
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              data-testid="image-file-input"
            />

            {/* AI Suggest button */}
            {image && !analyzing && (
              <button
                onClick={handleAiSuggest}
                className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sage/10 text-sage border border-sage/20 rounded-lg text-sm font-medium hover:bg-sage/15 transition-colors"
                data-testid="ai-suggest-btn"
              >
                <Sparkles size={16} />
                {aiSuggestedOnce ? 'Re-analyze with AI' : 'Suggest title, description, tags & products with AI'}
              </button>
            )}
            {analyzing && (
              <div className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sage/5 text-sage rounded-lg text-sm">
                <Loader2 size={16} className="animate-spin" />
                Analyzing your design...
              </div>
            )}
          </div>

          {/* Share with Community toggle */}
          <div className="flex items-center justify-between p-3 bg-cream rounded-lg" data-testid="share-community-section">
            <div>
              <p className="text-sm font-medium text-charcoal">Share with Community</p>
              <p className="text-xs text-gray-soft">Make this design visible in the community feed</p>
            </div>
            <button
              onClick={() => setIsPublic(!isPublic)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                isPublic ? 'bg-sage' : 'bg-beige'
              }`}
              data-testid="public-toggle"
              role="switch"
              aria-checked={isPublic}
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                isPublic ? 'translate-x-5' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Spring Watercolor Birthday Card"
              maxLength={200}
              className="w-full px-3 py-2.5 border border-beige rounded-lg text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
              data-testid="design-title-input"
            />
          </div>

          {/* Occasion */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2 flex items-center gap-2">
              <Calendar size={16} className="text-sage" />
              Occasion
            </label>
            <select
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
              className="w-full px-3 py-2.5 border border-beige rounded-lg text-charcoal focus:outline-none focus:border-sage bg-white"
              data-testid="design-occasion-select"
            >
              {occasions.map((occ) => (
                <option key={occ.value} value={occ.value}>
                  {occ.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell others about your design, techniques used, inspiration, etc."
              rows={3}
              maxLength={2000}
              className="w-full px-3 py-2.5 border border-beige rounded-lg text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage resize-none"
              data-testid="design-description-input"
            />
          </div>

          {/* Technique Tags */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">
              <Tag size={14} className="inline mr-1" />
              Technique Tags
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((tag) => (
                <span key={tag} className="px-2 py-1 bg-sage/10 text-sage text-xs rounded-full flex items-center gap-1">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-red-500"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="relative">
              <input
                ref={tagInputRef}
                type="text"
                value={tagInput}
                onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                onKeyDown={handleTagKeyDown}
                onFocus={() => setShowTagSuggestions(true)}
                onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                placeholder={tags.length < 10 ? 'Type a tag and press Enter' : 'Max 10 tags'}
                disabled={tags.length >= 10}
                className="w-full px-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                data-testid="tag-input"
              />
              {showTagSuggestions && tagSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-beige rounded-lg shadow-lg max-h-32 overflow-y-auto z-10">
                  {tagSuggestions.map((s) => (
                    <button key={s} onMouseDown={(e) => { e.preventDefault(); addTag(s); }} className="w-full text-left px-3 py-1.5 text-sm text-charcoal hover:bg-cream">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Products Used */}
          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">
              <Package size={14} className="inline mr-1" />
              Products Used
              <span className="font-normal text-gray-soft ml-1">({products.length}/20)</span>
            </label>

            {/* Current products list */}
            {products.length > 0 && (
              <div className="space-y-1.5 mb-3" data-testid="products-list">
                {products.map((p, idx) => (
                  <div
                    key={`${p.name}-${idx}`}
                    className="flex items-center gap-2 px-2.5 py-1.5 bg-cream/70 rounded-lg group"
                    data-testid={`product-item-${idx}`}
                  >
                    <span className="flex-1 text-sm text-charcoal truncate">
                      {p.name}
                      {p.brand && <span className="text-gray-soft text-xs ml-1">by {p.brand}</span>}
                    </span>
                    {sourceBadge(p.source)}
                    <button
                      onClick={() => removeProduct(idx)}
                      className="shrink-0 p-0.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      data-testid={`remove-product-${idx}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* AI suggest hint (if no products and image present) */}
            {products.length === 0 && image && !aiSuggestedOnce && (
              <div className="flex items-center gap-2 px-3 py-2.5 mb-3 bg-rose/5 border border-rose/15 rounded-lg">
                <Wand2 size={14} className="text-rose shrink-0" />
                <p className="text-xs text-rose-dark">
                  Use <strong>AI Suggest</strong> above to auto-detect products from your photo
                </p>
              </div>
            )}

            {/* Search stash to add */}
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                placeholder="Search your stash to add..."
                className="w-full pl-8 pr-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                data-testid="product-search-input"
              />
              {showProductDropdown && productSearch.trim() && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-beige rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
                  {filteredStash.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-soft">
                      {stashItems.length === 0 ? 'No items in your stash yet' : 'No matching items'}
                    </p>
                  ) : (
                    filteredStash.slice(0, 15).map((item) => (
                      <button
                        key={item.item_id}
                        onMouseDown={(e) => { e.preventDefault(); addStashProduct(item); }}
                        className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-cream"
                      >
                        <div className="truncate">
                          <span className="text-charcoal">{item.name}</span>
                          {item.brand && <span className="text-gray-soft ml-1 text-xs">by {item.brand}</span>}
                        </div>
                        <Boxes size={12} className="text-sage shrink-0 ml-2" />
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Manual product input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={manualProductInput}
                onChange={(e) => setManualProductInput(e.target.value)}
                onKeyDown={handleManualProductKeyDown}
                placeholder="Or type a product name..."
                className="flex-1 px-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                data-testid="manual-product-input"
              />
              <button
                onClick={addManualProduct}
                disabled={!manualProductInput.trim() || products.length >= 20}
                className="px-3 py-2 border border-beige rounded-lg text-sm text-gray-soft hover:text-charcoal hover:border-sage disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="add-manual-product-btn"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500" data-testid="create-design-error">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-beige flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium text-gray-soft border border-beige hover:bg-cream transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !title.trim()}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              !submitting && title.trim()
                ? 'bg-sage text-white hover:bg-sage-dark'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="create-design-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating...
              </>
            ) : (
              'Create Design'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDesignModal;
